import React, { useRef, forwardRef, useCallback } from 'react'
import { Box, Typography, Stack, Button } from '@mui/material'
import { useIsMobile } from '@/hooks/Responsive'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useNavigate } from 'react-router-dom'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { cleanSearchHtml } from '@/api/util'

export default function SearchDetailList({
  title,
  count,
  items,
  hasMore,
  onLoadMore,
  loading,
  loadingMore,
}) {
  const { isMobile } = useIsMobile()
  const firstNewItemRef = useRef(null)

  const navigate = useNavigate()
  const lang = useLang()

  const onItemClick = useCallback(
    (row) => {
      const tab = row?.tab

      if (tab === 'course') {
        navigate(buildLangHref(`/contents/lectureview/${row.id}`, lang), { state: { row } })
        return
      }

      if (tab === 'xr') {
        navigate(buildLangHref(`/contents/xrview/${row.id}`, lang), { state: { row } })
        return
      }

      if (tab === 'notice') {
        navigate('/alarm/noticeview', {
          state: {
            searchYn: 'Y',
            navParamData: { page: 1, pageSize: 10, recordSize: 10, searchNm: '' },
            rowData: { bbsId: row.id },
          },
        })
        return
      }

      if (tab === 'faq') {
        navigate('/alarm/faq', {
          state: {
            activeTab: row?.faqCategory ?? '',
            openId: row?.id,
          },
        })
        return
      }
    },
    [navigate, lang]
  )

  const hasItems = items && items.length > 0

  // 검색 결과 없음
  if (!hasItems && !loading)
    return (
      <Box>
        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={700} sx={{ mb: 3 }}>
          {title} 0건
        </Typography>
        <Stack
          alignItems="center"
          spacing={1.5}
          sx={{ px: 6, py: 4, mb: 8, bgcolor: 'grey.50', borderRadius: 4 }}
        >
          <ErrorOutlineIcon sx={{ fontSize: '32px', color: 'grey.light' }} />
          <Typography color="grey.500">검색결과가 없습니다.</Typography>
        </Stack>
      </Box>
    )

  // 로딩 중(초기)
  if (loading)
    return (
      <Box>
        <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={700} sx={{ mb: 3 }}>
          {title}{' '}
          <Typography component="span" variant={isMobile ? 'subtitle2' : 'subtitle1'} color="primary.main" fontWeight={700}>
            {count}
          </Typography>
          건
        </Typography>
        <Typography sx={{ color: 'text.secondary' }}>불러오는 중...</Typography>
      </Box>
    )

  const handleLoadMore = async () => {
    if (!onLoadMore || loadingMore) return
    onLoadMore()

    setTimeout(() => {
      if (firstNewItemRef.current) firstNewItemRef.current.focus()
    }, 50)
  }

  return (
    <Box>
      <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight={700} sx={{ mb: 3 }}>
        {title}{' '}
        <Typography component="span" variant={isMobile ? 'subtitle2' : 'subtitle1'} color="primary.main" fontWeight={700}>
          {count}
        </Typography>
        건
      </Typography>

      <Stack spacing={3} sx={{ mt: 3 }}>
        {items.map((item, index) => {
          const isFirstNew = index === items.length - 10
          return (
            <InlineItemCard
              key={`${item.tab}-${item.id}`}
              item={item}
              ref={isFirstNew ? firstNewItemRef : null}
              onClick={() => onItemClick(item)}
            />
          )
        })}
      </Stack>

      {hasMore && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            size="large"
            color="grey"
            onClick={handleLoadMore}
            endIcon={<KeyboardArrowDownIcon />}
            disabled={loadingMore}
            sx={{ border: 0, '&:hover': { border: 0 } }}
          >
            {loadingMore ? '불러오는 중...' : '더보기'}
          </Button>
        </Box>
      )}
    </Box>
  )
}

const InlineItemCard = forwardRef(function InlineItemCard({ item, onClick }, ref) {
  const { isMobile } = useIsMobile()

  return (
    <Stack spacing={3}>
      <Stack
        tabIndex={0}
        role="button"
        ref={ref}
        spacing={1.5}
        onClick={(e) => {
          e.stopPropagation()
          onClick?.()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            onClick?.()
          }
        }}
        sx={{
          bgcolor: 'grey.50',
          borderRadius: 4,
          px: 6,
          py: 4,
          color: 'text.primary',
          textDecoration: 'none',
          cursor: 'pointer',
          outline: 'none',
          '&:hover': { textDecoration: 'underline' },
          '&:focus-visible': { boxShadow: '0 0 0 2px rgba(0,0,0,0.25)' },
        }}
      >
        <Typography
          variant={isMobile ? 'subtitle3' : 'subtitle2'}
          fontWeight={500}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: isMobile ? 2 : 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.title}
        </Typography>

        <Typography
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: isMobile ? 2 : 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          dangerouslySetInnerHTML={{ __html: cleanSearchHtml(item.description) }}
        >
        </Typography>
      </Stack>
    </Stack>
  )
})
